
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Users, MapPin, Star, Bell, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EarlyEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  duration: string;
  location: string;
  type: 'internship' | 'contest' | 'workshop' | 'networking';
  spotsLeft: number;
  totalSpots: number;
  priority: 'high' | 'medium' | 'low';
  registered: boolean;
}

const EarlyAccessEvents: React.FC = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState<EarlyEvent[]>([]);
  const [notifications, setNotifications] = useState(true);

  const registerForEvent = (eventId: string) => {
    setEvents(events.map(event => 
      event.id === eventId 
        ? { ...event, registered: true, spotsLeft: event.spotsLeft - 1 }
        : event
    ));
    toast({
      title: "Registered Successfully!",
      description: "You've been registered for this exclusive event."
    });
  };

  const unregisterFromEvent = (eventId: string) => {
    setEvents(events.map(event => 
      event.id === eventId 
        ? { ...event, registered: false, spotsLeft: event.spotsLeft + 1 }
        : event
    ));
    toast({
      title: "Registration Cancelled",
      description: "Your registration has been cancelled."
    });
  };

  const toggleNotifications = () => {
    setNotifications(!notifications);
    toast({
      title: notifications ? "Notifications Disabled" : "Notifications Enabled",
      description: notifications 
        ? "You won't receive notifications for new events" 
        : "You'll be notified about new exclusive events"
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'internship': return 'bg-blue-100 text-blue-800';
      case 'contest': return 'bg-purple-100 text-purple-800';
      case 'workshop': return 'bg-green-100 text-green-800';
      case 'networking': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-pink-500" />
            Early Access to Events & Opportunities
          </CardTitle>
          <CardDescription>
            Get priority access to exclusive internships, contests, and premium events before they're available to the general community.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Notification Settings */}
          <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg border border-pink-200">
            <div>
              <h4 className="font-medium">Event Notifications</h4>
              <p className="text-sm text-muted-foreground">Get notified about new exclusive opportunities</p>
            </div>
            <Button 
              variant={notifications ? "default" : "outline"}
              onClick={toggleNotifications}
              className={notifications ? "bg-pink-500 hover:bg-pink-600" : ""}
            >
              <Bell className="h-4 w-4 mr-2" />
              {notifications ? 'Enabled' : 'Disabled'}
            </Button>
          </div>

          {/* Early Access Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-pink-200 bg-pink-50">
              <CardContent className="p-4 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-pink-600" />
                <div className="text-lg font-bold text-pink-700">24-48hr</div>
                <p className="text-sm text-pink-600">Early Access Window</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <div className="text-lg font-bold text-blue-700">Limited</div>
                <p className="text-sm text-blue-600">Exclusive Spots</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Star className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <div className="text-lg font-bold text-green-700">Premium</div>
                <p className="text-sm text-green-600">Quality Events</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 text-center">
                <Bell className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                <div className="text-lg font-bold text-orange-700">Instant</div>
                <p className="text-sm text-orange-600">Notifications</p>
              </CardContent>
            </Card>
          </div>

          {/* Events List */}
          <div>
            <h4 className="font-semibold mb-4">Exclusive Early Access Events</h4>
            {events.length === 0 ? (
              <div className="text-center py-8">
                <Star className="h-12 w-12 mx-auto mb-4 text-pink-500" />
                <h3 className="text-lg font-medium mb-2">No Early Access Events Available</h3>
                <p className="text-muted-foreground mb-4">
                  You'll be notified as soon as new exclusive opportunities become available.
                </p>
                <Button onClick={toggleNotifications} className="bg-pink-500 hover:bg-pink-600">
                  <Bell className="h-4 w-4 mr-2" />
                  Enable Notifications
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <Card key={event.id} className="border-l-4 border-l-pink-500">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold">{event.title}</h3>
                          <div className="flex gap-2 mt-1">
                            <Badge className={getTypeColor(event.type)}>
                              {event.type}
                            </Badge>
                            <Badge className={getPriorityColor(event.priority)}>
                              {event.priority} priority
                            </Badge>
                          </div>
                        </div>
                        <Badge variant={event.registered ? 'default' : 'outline'}>
                          {event.registered ? 'Registered' : `${event.spotsLeft}/${event.totalSpots} spots`}
                        </Badge>
                      </div>

                      <p className="text-muted-foreground mb-4">{event.description}</p>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{event.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{event.location}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {event.registered ? (
                          <Button 
                            variant="outline"
                            onClick={() => unregisterFromEvent(event.id)}
                            className="border-red-500 text-red-500 hover:bg-red-50"
                          >
                            Cancel Registration
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => registerForEvent(event.id)}
                            className="bg-pink-500 hover:bg-pink-600"
                            disabled={event.spotsLeft === 0}
                          >
                            {event.spotsLeft === 0 ? 'Fully Booked' : 'Register Now'}
                          </Button>
                        )}
                        <Button variant="outline">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EarlyAccessEvents;
